<template>
  <div class="side-bar sticky flex shrink-0 flex-col gap-5 bg-white rounded p-5 h-full w-374px xl:w-512px transition-top overflow-auto no-scrollbar">
    <div class="hero-image relative">
      <nd-img class="w-full aspect-1.8 object-cover rounded" :src="`fn:${props.product?.cover_image}`" :alt="props.product?.product_name" />
      <div class="overlay absolute top-0 left-0 w-full h-full z-10 pointer-events-none">
        <favorite-button
          v-if="props.product.pid"
          :id="props.product.pid"
          :is-favorited="props.product?.is_favorite || false"
          class="absolute top-2 right-2 pointer-events-auto"
        />
      </div>
    </div>
    <locale-link :to="`/regions/${regionId}/branches/${props.product?.bid}`">
      <span class="text-lg text-yellow-50">{{ props.product?.branch_name }}</span>
    </locale-link>
    <h2 class="text-2xl">{{ props.product?.product_name }}</h2>
    <div class="sub-info flex items-center gap-2">
      <div v-if="props.productComments?.total_count" class="rating flex gap-2">
        <svg-icon name="star-filled" class="w-6 h-6 text-yellow-50" />
        <span class="text-lg text-black-50">{{ props.productComments?.avg_rating?.toFixed(1) }}</span>
        <span class="text-base text-black-50">({{ props.productComments?.total_count }})</span>
      </div>
      <div v-if="props.productComments?.total_count && isDistanceShown" class="vertical-divider text-lg">|</div>
      <div v-if="isDistanceShown" class="text-lg">{{ $t('distance_from_you', { distance_from_you: props.product.distance }) }}</div>
    </div>
    <hr />
    <reservation-date-time-picker v-if="!isSoldOut" v-model="bookingItem" :product="props.product" />
    <reservation-addition-and-upgrade
      v-if="bookingItem.length"
      v-model:additions="additions"
      v-model:upgrades="upgrades"
      :product="props.product"
      :booking-item="bookingItem"
    />
    <reservation-summary
      class="summary-bar -mx-5 -mb-5 px-5 py-6"
      :product="props.product"
      :booking-item="bookingItem"
      :additions="additions"
      :upgrades="upgrades"
    />
  </div>
</template>

<script setup lang="ts">
import FavoriteButton from '@/components/common/favorite-button.vue'
import ReservationDateTimePicker from '@/components/reservation/reservation-date-time-picker.vue'
import ReservationAdditionAndUpgrade from '@/components/reservation/reservation-addition-and-upgrade.vue'
import ReservationSummary from '@/components/reservation/reservation-summary.vue'
import type { UiProductInfo, UiProductSelectedData } from '@/types/product'
import type { ProductUpgradeResource, ProductAdditionsResource } from '@/types/booking'
import type { FreviewReviewResponse } from '@/types/api'

const bookingItem = defineModel<UiProductSelectedData[]>('bookingItem', { default: [] })
const additions = defineModel<ProductAdditionsResource[]>('additions', { default: [] })
const upgrades = defineModel<ProductUpgradeResource[]>('upgrades', { default: [] })

const props = defineProps<{
  product: UiProductInfo
  productComments?: FreviewReviewResponse
}>()

const { regionId } = useRegion()

const isDistanceShown = computed(() => {
  if (!props.product?.distance) return false
  return parseFloat(props.product.distance || '') <= 10
})

const isSoldOut = inject<ComputedRef<boolean>>('isSoldOut')
</script>

<style lang="scss" scoped>
.summary-bar {
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
}
</style>
